/**
 * UserReputationCard — Composant réutilisable
 *
 * Affiche l'avatar (photo ou initiales), le nom, la date d'adhésion,
 * la note globale avec les 5 étoiles colorées, et ouvre le modal au clic.
 *
 * Performance : fetch déclenché uniquement quand la card entre dans le
 * viewport (IntersectionObserver) — zéro appel au montage.
 *
 * Props :
 *   userId       {string}  — ID de l'utilisateur (obligatoire)
 *   user         {object}  — Données de base (name, first_name, last_name,
 *                            profile_picture, created_at…) déjà disponibles
 *   fallbackName {string}  — Nom affiché si `user` est vide
 *   sx           {object}  — Styles MUI supplémentaires sur le conteneur
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Avatar,
  Typography,
  Rating,
  useTheme,
  alpha
} from '@mui/material';
import { CalendarToday as CalendarIcon, Star as StarIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import api from '../../services/api';
import UserReputationModal from './UserReputationModal';

const UserReputationCard = ({ userId, user = null, fallbackName = '', sx = {} }) => {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const cardRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [reputation, setReputation] = useState(null); // { score, totalReviews, profilePicture, memberSince, name }
  const [fetched, setFetched] = useState(false);

  /* --- Données statiques depuis la prop `user` (pas de fetch) --- */
  const displayName =
    user?.name ||
    `${user?.first_name || user?.firstName || ''} ${user?.last_name || user?.lastName || ''}`.trim() ||
    fallbackName ||
    '?';

  // Photo depuis la prop user (souvent absente des jointures reservation)
  // Complétée après fetch si dispo dans la réponse API
  const avatarSrc =
    reputation?.profilePicture ||
    user?.profile_picture || user?.profilePicture || user?.avatar || undefined;

  const memberSinceRaw =
    reputation?.memberSince ||
    user?.memberSince || user?.createdAt || user?.created_at;

  const memberSinceLabel = memberSinceRaw
    ? new Date(memberSinceRaw).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  const score = reputation?.score ?? 0;
  const totalReviews = reputation?.totalReviews ?? 0;

  /* --- Fetch lazy via IntersectionObserver --- */
  useEffect(() => {
    if (!userId || fetched) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          setFetched(true);
          api
            .get(`/users/${userId}/reputation`)
            .then((res) => {
              const data = res.data.data;
              setReputation({
                score: data.reputation?.score ?? 0,
                totalReviews: data.reputation?.totalReviews ?? 0,
                profilePicture: data.user?.profilePicture || null,
                memberSince: data.user?.memberSince || null,
              });
            })
            .catch((err) =>
              console.error('[UserReputationCard] fetch error:', err)
            );
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [userId, fetched]);

  if (!userId) return null;

  const initial = displayName.charAt(0).toUpperCase();

  return (
    <>
      <Box
        ref={cardRef}
        onClick={() => setModalOpen(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: 2,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[2],
          },
          ...sx,
        }}
      >
        {/* Avatar : photo si dispo, sinon initiale colorée */}
        <Avatar
          src={avatarSrc || undefined}
          alt={displayName}
          sx={{
            width: 44,
            height: 44,
            bgcolor: 'primary.main',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            flexShrink: 0,
          }}
        >
          {!avatarSrc && initial}
        </Avatar>

        {/* Infos texte */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight="bold" noWrap>
            {displayName}
          </Typography>

          {memberSinceLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}
            >
              <CalendarIcon sx={{ fontSize: 12, mr: 0.5 }} />
              {t('myReservations.reputation.memberSince', 'Membre depuis ')} {memberSinceLabel}
            </Typography>
          )}

          {/* 5 étoiles colorées + note + lien */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Rating
              value={score}
              precision={0.1}
              readOnly
              size="small"
              sx={{ fontSize: '0.85rem' }}
            />
            {score > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                {score.toFixed(1)}{totalReviews > 0 && ` (${totalReviews})`}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1, fontStyle: 'italic' }}>
                {t('myReservations.reputation.noReviews', 'Pas encore noté')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 'auto' }}>
              <VisibilityIcon sx={{ fontSize: 15, color: theme.palette.primary.main }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Modal — ouvre seulement au clic */}
      <UserReputationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        userId={userId}
      />
    </>
  );
};

export default UserReputationCard;
